import { AgingAR } from '../../hooks/useKPIs'

interface AgingARBarsProps {
  aging: AgingAR
}

function formatEur(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

type LegacyAgingAR = {
  bucket_0_30?: number
  bucket_31_60?: number
  bucket_61_90?: number
  bucket_90_plus?: number
}

const BUCKETS = [
  {
    key: '1_30' as const,
    legacyKey: 'bucket_0_30' as const,
    label: '0–30 giorni',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-400',
    severity: 'Corrente',
  },
  {
    key: '31_60' as const,
    legacyKey: 'bucket_31_60' as const,
    label: '31–60 giorni',
    color: 'bg-amber-500',
    textColor: 'text-amber-400',
    severity: 'Attenzione',
  },
  {
    key: '61_90' as const,
    legacyKey: 'bucket_61_90' as const,
    label: '61–90 giorni',
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    severity: 'Sollecito',
  },
  {
    key: '90_plus' as const,
    legacyKey: 'bucket_90_plus' as const,
    label: '> 90 giorni',
    color: 'bg-red-500',
    textColor: 'text-red-400',
    severity: 'Critico',
  },
]

function amountForBucket(
  aging: AgingAR | LegacyAgingAR,
  key: keyof AgingAR,
  legacyKey: keyof LegacyAgingAR,
): number {
  const bucket = (aging as AgingAR)[key]
  if (bucket && typeof bucket === 'object' && 'amount' in bucket) {
    return Number(bucket.amount) || 0
  }
  return Number((aging as LegacyAgingAR)[legacyKey]) || 0
}

export function AgingARBars({ aging }: AgingARBarsProps) {
  const total = BUCKETS.reduce((sum, bucket) => {
    return sum + amountForBucket(aging, bucket.key, bucket.legacyKey)
  }, 0)

  if (total === 0) {
    return (
      <p className="text-sm text-[--km-text-muted] py-4" data-testid="aging-ar-empty">
        Nessun credito aperto.
      </p>
    )
  }

  return (
    <div className="space-y-4" data-testid="aging-ar-bars">
      {BUCKETS.map((b) => {
        const amount = amountForBucket(aging, b.key, b.legacyKey)
        const pct    = (amount / total) * 100

        return (
          <div key={b.key} className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className={`font-medium ${b.textColor}`}>{b.label}</span>
                <span className="text-[--km-text-muted]">· {b.severity}</span>
              </div>
              <span className={`font-semibold tabular-nums ${b.textColor}`}>
                {formatEur(amount)}
              </span>
            </div>

            <div className="h-2 w-full rounded-full bg-[--km-surface-3] overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${b.color}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
                role="progressbar"
                aria-valuenow={Math.round(pct)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={b.label}
              />
            </div>

            <p className="text-[10px] text-[--km-text-muted] text-right">
              {pct.toFixed(0)}% del totale
            </p>
          </div>
        )
      })}

      {/* Totale */}
      <div className="pt-3 border-t border-[--km-border] flex items-center justify-between text-xs">
        <span className="text-[--km-text-muted]">Totale crediti aperti</span>
        <span className="font-semibold text-[--km-text-primary] tabular-nums">
          {formatEur(total)}
        </span>
      </div>
    </div>
  )
}
